// Asset Register Form JavaScript Logic

// Enhanced Form Submission Manager for Asset Register
class AssetRegisterFormSubmissionManager extends FormSubmissionManager {
    constructor() {
        super('assetRegisterForm');
        this.initAntiSpam();
    }

    initAntiSpam() {
        // Set form load time for anti-spam checking
        const formLoadTimeField = document.getElementById('form_load_time');
        if (formLoadTimeField) {
            formLoadTimeField.value = new Date().toISOString();
        }
    }

    async handleSubmit(event) {
        event.preventDefault();
        
        // Prevent double submission
        if (this.isSubmitting) return;
        
        // Validate form before showing loading
        if (!this.validateAssetRegisterForm()) {
            this.showWarning('Please fill in all required fields and upload mandatory documents before submitting.');
            return;
        }

        // Show loading with initial message
        this.showLoading('Processing Asset Register', 'Preparing your submission...');
        
        try {
            // Step 1: Process files
            this.updateProgress(10, 'Processing uploaded files...');
            const formData = new FormData(this.form);
            const fileSummary = getFileListSummary();
            const fileData = await processAllFiles(formData);
            
            this.updateProgress(30, `Processed ${fileData.fileCount} files (${(fileData.totalSize / 1024 / 1024).toFixed(2)} MB)`);
            
            // Step 2: Prepare submission data
            this.updateProgress(50, 'Preparing submission data...');
            const webhookData = this.prepareWebhookData(formData, fileData);
            
            // Step 3: Submit to webhook proxy (which also saves to database and generates PDF)
            this.updateProgress(70, 'Submitting your data...');
            const webhookResult = await this.makeRequest('/Forms/AssetRegisterWebhookProxy', webhookData);
            
            if (!webhookResult.success) {
                throw new Error(webhookResult.error || 'Failed to submit asset register');
            }
            
            this.updateProgress(90, 'Submission complete!');
            
            // Step 4: Success
            this.updateProgress(100, 'Redirecting...');
            
            // Reset form
            this.form.reset();
            updateFormSummary();
            
            // Show success and redirect
            this.showSuccess('Your Asset Register has been submitted successfully!');
            
        } catch (error) {
            console.error('Asset Register submission failed:', error);
            
            // Determine error type and show appropriate message
            if (error.message.includes('database') || error.message.includes('save')) {
                this.showError(
                    'There was a problem saving your submission to our database. Please try again or contact support if the problem persists.',
                    true
                );
            } else if (error.message.includes('network') || error.message.includes('fetch')) {
                this.showError(
                    'There was a network connection problem. Please check your internet connection and try again.',
                    true
                );
            } else {
                this.showError(
                    'An unexpected error occurred while processing your submission. Please try again or contact support if the problem persists.',
                    true
                );
            }
        }
    }

    validateAssetRegisterForm() {
        const requiredFields = document.querySelectorAll('input[required], select[required], textarea[required]');
        let isValid = true;
        let firstInvalidField = null;

        requiredFields.forEach(field => {
            if (!field.value.trim()) {
                field.style.borderColor = '#dc2626';
                field.style.backgroundColor = '#fef2f2';
                if (!firstInvalidField) firstInvalidField = field;
                isValid = false;
            } else {
                field.style.borderColor = '#d1d5db';
                field.style.backgroundColor = '#ffffff';
            }
        });

        // Validate purchase date format
        const purchaseDate = document.getElementById('purchaseDate');
        if (purchaseDate && purchaseDate.value && !validateDateFormat(purchaseDate.value)) {
            purchaseDate.style.borderColor = '#dc2626';
            purchaseDate.style.backgroundColor = '#fef2f2';
            if (!firstInvalidField) firstInvalidField = purchaseDate;
            isValid = false;
        }

        // Validate file uploads for financed assets
        const financedElement = document.getElementById('financed');
        if (financedElement && financedElement.value === 'yes') {
            const taxInvoice = document.getElementById('taxInvoice');
            const financeContract = document.getElementById('financeContract');
            
            if (taxInvoice && !taxInvoice.files.length) {
                taxInvoice.style.borderColor = '#dc2626';
                if (!firstInvalidField) firstInvalidField = taxInvoice;
                isValid = false;
            }
            
            if (financeContract && !financeContract.files.length) {
                financeContract.style.borderColor = '#dc2626';
                if (!firstInvalidField) firstInvalidField = financeContract;
                isValid = false;
            }
        }

        if (!isValid && firstInvalidField) {
            firstInvalidField.focus();
            firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        return isValid;
    }

    prepareSubmissionData(formData, fileData) {
        return {
            BuyerName: formData.get('buyerName') || '',
            BuyerABN: formData.get('buyerABN') || '',
            BusinessId: formData.get('businessId') || '',
            SellerDetails: formData.get('sellerDetails') || '',
            PurchaseDate: formData.get('purchaseDate') || '',
            AcquisitionPurchaseDate: formData.get('acquisitionPurchaseDate') ? convertToISODate(formData.get('acquisitionPurchaseDate')) : null,
            PurchaseAmount: formData.get('purchaseAmount') ? parseFloat(formData.get('purchaseAmount')) : null,
            DepositPaid: formData.get('depositPaid') ? parseFloat(formData.get('depositPaid')) : null,
            AssetType: formData.get('assetType') || '',
            OtherAssetTypeName: formData.get('otherAssetTypeName') || '',
            PaidCash: formData.get('paidCash') === 'yes',
            CashAmount: formData.get('cashAmount') ? parseFloat(formData.get('cashAmount')) : 0,
            Financed: formData.get('financed') === 'yes',
            Insured: formData.get('insured') === 'yes',
            RequiresRegistration: formData.get('requiresRegistration') === 'yes',
            WorkDescription: formData.get('workDescription') || '',
            UploadedFiles: Object.keys(fileData.files),
            UploadedFilesData: JSON.stringify(fileData.files)
        };
    }

    prepareWebhookData(formData, fileData) {
        // Helper function to parse decimal values
        const parseDecimal = (value) => {
            if (!value || value === '') return null;
            const parsed = parseFloat(value);
            return isNaN(parsed) ? null : parsed;
        };

        return {
            form_type: 'Asset Register',
            buyer_name: formData.get('buyerName') || '',
            buyer_abn: formData.get('buyerABN') || '',
            business_id: formData.get('businessId') || '',
            seller_details: formData.get('sellerDetails') || '',
            purchase_date: formData.get('purchaseDate') || '',
            acquisition_purchase_date: formData.get('acquisitionPurchaseDate') || null,
            purchase_amount: parseDecimal(formData.get('purchaseAmount')),
            deposit_paid: parseDecimal(formData.get('depositPaid')),
            asset_type: formData.get('assetType') || '',
            asset_description: formData.get('assetDescription') || '',
            other_asset_type: formData.get('otherAssetTypeName') || '',
            paid_cash: formData.get('paidCash') || 'no',
            cash_amount: parseDecimal(formData.get('cashAmount')),
            financed: formData.get('financed') || 'no',
            insured: formData.get('insured') || 'no',
            requires_registration: formData.get('requiresRegistration') || 'no',
            work_description: formData.get('workDescription') || '',
            uploaded_files: fileData.files,
            files_summary: {
                total_files: fileData.fileCount,
                total_size_bytes: fileData.totalSize,
                total_size_mb: (fileData.totalSize / 1024 / 1024).toFixed(2),
                file_categories: Object.keys(fileData.files)
            },
            submission_date: new Date().toISOString(),
            timestamp: Date.now()
        };
    }
}

// Global variables and functions (keep existing functionality)
window.toggleOtherAssetType = function() {
    const assetTypeElement = document.getElementById('assetType');
    if (!assetTypeElement) return;
    
    const assetType = assetTypeElement.value;
    const otherField = document.getElementById('otherAssetTypeField');
    const otherInput = document.getElementById('otherAssetTypeName');
    
    if (!otherField || !otherInput) return;
    
    if (assetType === 'other') {
        otherField.classList.remove('hidden');
        otherInput.required = true;
    } else {
        otherField.classList.add('hidden');
        otherInput.required = false;
        otherInput.value = '';
    }
    updateFormSummary();
}

window.toggleCashAmountField = function() {
    const paidCashElement = document.getElementById('paidCash');
    if (!paidCashElement) return;
    
    const paidCash = paidCashElement.value;
    const cashAmountField = document.getElementById('cashAmountField');
    const cashAmountInput = document.getElementById('cashAmount');
    
    if (!cashAmountField || !cashAmountInput) return;
    
    if (paidCash === 'yes') {
        cashAmountField.classList.remove('hidden');
        cashAmountInput.required = true;
    } else {
        cashAmountField.classList.add('hidden');
        cashAmountInput.required = false;
        cashAmountInput.value = '';
    }
    updateFormSummary();
}

window.toggleFinanceFields = function() {
    const financedElement = document.getElementById('financed');
    if (!financedElement) return;
    
    const financed = financedElement.value;
    const financeFields = document.getElementById('financeFields');
    const taxInvoice = document.getElementById('taxInvoice');
    const financeContract = document.getElementById('financeContract');
    
    if (!financeFields || !taxInvoice || !financeContract) return;
    
    if (financed === 'yes') {
        financeFields.classList.remove('hidden');
        taxInvoice.required = true;
        financeContract.required = true;
    } else {
        financeFields.classList.add('hidden');
        taxInvoice.required = false;
        financeContract.required = false;
        document.querySelectorAll('#financeFields input[type="file"]').forEach(input => {
            input.value = '';
        });
    }
    updateFormSummary();
}

window.toggleInsuranceFields = function() {
    const insuredElement = document.getElementById('insured');
    if (!insuredElement) return;
    
    const insured = insuredElement.value;
    const insuranceFields = document.getElementById('insuranceFields');
    
    if (!insuranceFields) return;
    
    if (insured === 'yes') {
        insuranceFields.classList.remove('hidden');
    } else {
        insuranceFields.classList.add('hidden');
        document.querySelectorAll('#insuranceFields input[type="file"]').forEach(input => {
            input.value = '';
        });
    }
    updateFormSummary();
}

window.toggleRegistrationFields = function() {
    const requiresRegistrationElement = document.getElementById('requiresRegistration');
    if (!requiresRegistrationElement) return;
    
    const requiresRegistration = requiresRegistrationElement.value;
    const registrationFields = document.getElementById('registrationFields');
    
    if (!registrationFields) return;
    
    if (requiresRegistration === 'yes') {
        registrationFields.classList.remove('hidden');
    } else {
        registrationFields.classList.add('hidden');
        document.querySelectorAll('#registrationFields input[type="file"]').forEach(input => {
            input.value = '';
        });
    }
    updateFormSummary();
}

// Helper function to format dates in DD/MM/YYYY format
function formatAustralianDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
}

function updateFormSummary() {
    const summary = document.getElementById('formSummary');
    if (!summary) return;
    
    const buyerName = document.getElementById('buyerName')?.value || '';
    const buyerABN = document.getElementById('buyerABN')?.value || '';
    const purchaseDate = document.getElementById('purchaseDate')?.value || '';
    const assetType = document.getElementById('assetType')?.value || '';
    const otherAssetType = document.getElementById('otherAssetTypeName')?.value || '';
    
    // Acquisition Details
    const acquisitionPurchaseDate = document.getElementById('acquisitionPurchaseDate')?.value || '';
    const purchaseAmount = document.getElementById('purchaseAmount')?.value || '';
    const depositPaid = document.getElementById('depositPaid')?.value || '';
    
    const paidCash = document.getElementById('paidCash')?.value || '';
    const cashAmount = document.getElementById('cashAmount')?.value || '';
    const financed = document.getElementById('financed')?.value || '';
    const insured = document.getElementById('insured')?.value || '';
    const requiresRegistration = document.getElementById('requiresRegistration')?.value || '';

    let summaryText = '<div class="space-y-2">';
    
    if (buyerName) {
        summaryText += `<div><strong>Buyer:</strong> ${buyerName}</div>`;
    }
    
    if (buyerABN) {
        summaryText += `<div><strong>ABN:</strong> ${buyerABN}</div>`;
    }
    
    if (purchaseDate) {
        summaryText += `<div><strong>Purchase Date:</strong> ${purchaseDate}</div>`;
    }
    
    if (assetType) {
        let assetTypeText = assetType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
        if (assetType === 'other' && otherAssetType) {
            assetTypeText = otherAssetType;
        }
        summaryText += `<div><strong>Asset Type:</strong> ${assetTypeText}</div>`;
    }
    
    // Add Acquisition Details
    if (acquisitionPurchaseDate || purchaseAmount || depositPaid) {
        summaryText += `<div class="pt-2 border-t border-gray-200"><strong>Acquisition Details:</strong></div>`;
        
        if (acquisitionPurchaseDate) {
            const formattedDate = new Date(acquisitionPurchaseDate).toLocaleDateString('en-AU');
            summaryText += `<div class="ml-4"><strong>Purchase Date:</strong> ${formattedDate}</div>`;
        }
        
        if (purchaseAmount) {
            summaryText += `<div class="ml-4"><strong>Purchase Amount:</strong> $${parseFloat(purchaseAmount).toLocaleString('en-AU', {minimumFractionDigits: 2})}</div>`;
        }
        
        if (depositPaid) {
            summaryText += `<div class="ml-4"><strong>Deposit Paid:</strong> $${parseFloat(depositPaid).toLocaleString('en-AU', {minimumFractionDigits: 2})}</div>`;
        }
    }
    
    if (paidCash === 'yes' && cashAmount) {
        summaryText += `<div><strong>Cash Contribution:</strong> $${parseFloat(cashAmount).toLocaleString('en-AU', {minimumFractionDigits: 2})}</div>`;
    } else if (paidCash === 'no') {
        summaryText += `<div><strong>Cash Contribution:</strong> No cash payment</div>`;
    }
    
    if (financed) {
        summaryText += `<div><strong>Financed:</strong> ${financed === 'yes' ? 'Yes' : 'No'}</div>`;
    }
    
    if (insured) {
        summaryText += `<div><strong>Insured:</strong> ${insured === 'yes' ? 'Yes' : 'No'}</div>`;
    }
    
    if (requiresRegistration) {
        summaryText += `<div><strong>Registration Required:</strong> ${requiresRegistration === 'yes' ? 'Yes' : 'No'}</div>`;
    }
    
    summaryText += '</div>';
    
    if (summaryText === '<div class="space-y-2"></div>') {
        summaryText = '<p>Please complete the form fields above. A summary will appear here as you progress.</p>';
    }
    
    summary.innerHTML = summaryText;
}

function getFileListSummary() {
    const fileInputs = document.querySelectorAll('input[type="file"]');
    const fileSummary = {};
    
    fileInputs.forEach(input => {
        if (input.files.length > 0) {
            const files = Array.from(input.files).map(file => file.name);
            fileSummary[input.name] = files;
        }
    });
    
    return fileSummary;
}

// Date input formatting for DD/MM/YYYY
function formatDateInput(input) {
    let value = input.value.replace(/\D/g, ''); // Remove non-digits
    
    if (value.length >= 2) {
        value = value.substring(0, 2) + '/' + value.substring(2);
    }
    if (value.length >= 5) {
        value = value.substring(0, 5) + '/' + value.substring(5, 9);
    }
    
    input.value = value;
}

// ABN input formatting for XX XXX XXX XXX
function formatABNInput(input) {
    let value = input.value.replace(/\D/g, ''); // Remove non-digits
    
    if (value.length >= 2) {
        value = value.substring(0, 2) + ' ' + value.substring(2);
    }
    if (value.length >= 6) {
        value = value.substring(0, 6) + ' ' + value.substring(6);
    }
    if (value.length >= 10) {
        value = value.substring(0, 10) + ' ' + value.substring(10, 13);
    }
    
    input.value = value;
}

function validateDateFormat(dateString) {
    const regex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[012])\/([0-9]{4})$/;
    if (!regex.test(dateString)) return false;
    
    const [day, month, year] = dateString.split('/').map(Number);
    const date = new Date(year, month - 1, day);
    
    return date.getFullYear() === year && 
           date.getMonth() === month - 1 && 
           date.getDate() === day;
}

function convertToISODate(ddmmyyyy) {
    if (!ddmmyyyy || !validateDateFormat(ddmmyyyy)) return '';
    const [day, month, year] = ddmmyyyy.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}

// Function to convert file to base64
function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });
}

// Function to process all uploaded files
async function processAllFiles(formData) {
    const fileInputs = [
        'taxInvoice',
        'financeContract', 
        'amortisationSchedule',
        'otherFinanceDocuments',
        'insuranceCertificate',
        'policyDocuments',
        'otherInsuranceDocuments',
        'registrationDocuments',
        'otherRegistrationDocuments'
    ];
    
    const processedFiles = {};
    let totalFileSize = 0;
    const maxTotalFileSize = 10 * 1024 * 1024; // 10MB total limit
    const maxIndividualFileSize = 5 * 1024 * 1024; // 5MB per file limit
    const filesExceedingLimit = [];
    
    for (const inputName of fileInputs) {
        const files = formData.getAll(inputName);
        if (files && files.length > 0) {
            processedFiles[inputName] = [];
            
            for (const file of files) {
                if (file && file.size > 0) {
                    // Validate individual file size
                    if (file.size > maxIndividualFileSize) {
                        filesExceedingLimit.push(`${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
                        console.warn(`File ${file.name} exceeds 5MB limit: ${(file.size / 1024 / 1024).toFixed(2)} MB`);
                        continue; // Skip this file
                    }
                    
                    try {
                        const base64Data = await fileToBase64(file);
                        const fileInfo = {
                            name: file.name,
                            size: file.size,
                            type: file.type,
                            lastModified: file.lastModified,
                            base64: base64Data,
                            uploadTimestamp: new Date().toISOString()
                        };
                        processedFiles[inputName].push(fileInfo);
                        totalFileSize += file.size;
                        
                        console.log(`Processed file: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
                    } catch (error) {
                        console.error(`Error processing file ${file.name}:`, error);
                    }
                }
            }
            
            // Remove empty arrays
            if (processedFiles[inputName].length === 0) {
                delete processedFiles[inputName];
            }
        }
    }
    
    // Warn if files were skipped
    if (filesExceedingLimit.length > 0) {
        const message = `The following files exceed the 5MB limit and were skipped:\n${filesExceedingLimit.join('\n')}`;
        alert(message);
        console.warn('Files exceeding size limit:', filesExceedingLimit);
    }
    
    // Log and validate total file size
    const totalFileSizeMB = (totalFileSize / 1024 / 1024).toFixed(2);
    const fileCount = Object.values(processedFiles).reduce((count, files) => count + files.length, 0);
    
    console.log(`Total files processed: ${fileCount} files, ${totalFileSizeMB} MB`);
    
    if (totalFileSize > maxTotalFileSize) {
        const warningMessage = `Warning: Total file size (${totalFileSizeMB} MB) exceeds recommended limit of 10 MB. This may cause upload issues.\n\nDo you want to continue?`;
        if (!confirm(warningMessage)) {
            throw new Error('Upload cancelled by user due to file size');
        }
    }
    
    return {
        files: processedFiles,
        totalSize: totalFileSize,
        fileCount: fileCount
    };
}

// Initialize form with enhanced submission handling
let formSubmissionManager;

document.addEventListener('DOMContentLoaded', function() {
    console.log('Initializing Asset Register form...');
    
    // Initialize enhanced form submission manager
    formSubmissionManager = new AssetRegisterFormSubmissionManager();
    
    // Add event listeners for form summary updates
    const formFields = document.querySelectorAll('input, select, textarea');
    formFields.forEach(field => {
        field.addEventListener('change', updateFormSummary);
        if (field.type === 'text' || field.type === 'textarea') {
            field.addEventListener('input', updateFormSummary);
        }
    });

    // Add date formatting for purchase date
    const purchaseDateInput = document.getElementById('purchaseDate');
    if (purchaseDateInput) {
        purchaseDateInput.addEventListener('input', function() {
            formatDateInput(this);
            updateFormSummary();
        });
    }

    // Add ABN formatting
    const abnInput = document.getElementById('buyerABN');
    if (abnInput) {
        abnInput.addEventListener('input', function() {
            formatABNInput(this);
            updateFormSummary();
        });
    }
    
    console.log('Asset Register form initialized successfully with enhanced submission handling');
});

// Clean up on page unload
window.addEventListener('beforeunload', function() {
    if (formSubmissionManager) {
        formSubmissionManager.destroy();
    }
});
